import { Link } from 'react-router-dom';
import {
  Store,
  ClipboardList,
  Receipt,
  Cog,
  Printer,
  CreditCard,
  ArrowRight,
  ArrowLeftRight,
  TicketCheck,
  Sunrise,
  Sun,
  Sunset,
  Moon,
  Loader2,
  Gift,
} from 'lucide-react';
import { useShallow } from 'zustand/react/shallow';
import { MoneyValue } from '@/components/pos/MoneyValue';
import { StatusPill } from '@/components/pos/StatusPill';
import { useAuthStore } from '@/stores/auth.store';
import { useDeviceStore } from '@/stores/device.store';
import { useShiftSummary, useOrderLookup } from '@/hooks/queries';
import { todayISO, formatDate, formatTime, number } from '@/lib/format';
import { cn } from '@/lib/utils';

const EMPTY = { count: 0, gross: 0, discount: 0 };

/** Time-of-day greeting + its sun-phase icon (Asia/Jakarta local clock). */
function greetingFor(hour: number): { text: string; Icon: typeof Sun } {
  if (hour < 11) return { text: 'Selamat pagi', Icon: Sunrise };
  if (hour < 15) return { text: 'Selamat siang', Icon: Sun };
  if (hour < 19) return { text: 'Selamat sore', Icon: Sunset };
  return { text: 'Selamat malam', Icon: Moon };
}

/** A large touch target that launches one workflow. */
function ActionTile({
  to,
  icon: Icon,
  title,
  desc,
  primary = false,
  className,
}: {
  to: string;
  icon: typeof Store;
  title: string;
  desc: string;
  primary?: boolean;
  className?: string;
}) {
  return (
    <Link
      to={to}
      className={cn(
        'group relative flex flex-col gap-3 rounded-2xl border p-4 transition-all',
        className,
        'hover:-translate-y-0.5 hover:shadow-pop focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-500',
        primary
          ? 'border-transparent bg-gradient-to-br from-crimson-700 to-crimson-600 text-cream-50'
          : 'border-cream-200 bg-white text-ink-900 hover:border-gold-500/50',
      )}
    >
      <span
        className={cn(
          'flex h-11 w-11 items-center justify-center rounded-xl',
          primary ? 'bg-white/15 text-cream-50' : 'bg-cream-100 text-crimson-700',
        )}
      >
        <Icon className="h-5 w-5" aria-hidden="true" />
      </span>
      <div className="min-w-0">
        <p className="font-display text-lg font-semibold leading-tight">{title}</p>
        <p className={cn('text-xs', primary ? 'text-cream-100/80' : 'text-muted-foreground')}>{desc}</p>
      </div>
      <ArrowRight
        className={cn(
          'h-4 w-4 transition-transform group-hover:translate-x-0.5',
          primary ? 'text-cream-50/70' : 'text-ink-400',
        )}
        aria-hidden="true"
      />
    </Link>
  );
}

/** One method column in the takings band. */
function MethodStat({ label, count, gross }: { label: string; count: number; gross: number }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="font-sans text-[11px] font-semibold uppercase tracking-wide text-ink-400">{label}</span>
      <MoneyValue value={gross} className="text-sm" />
      <span className="tnum text-xs text-muted-foreground">{number(count)} transaksi</span>
    </div>
  );
}

export function MenuPage() {
  const user = useAuthStore((s) => s.user);
  const { printerBound, readerBound } = useDeviceStore(
    useShallow((s) => ({
      printerBound: Boolean(s.ticketPrinterId),
      readerBound: Boolean(s.cashlessReaderId),
    })),
  );

  const today = todayISO();
  const { data, isLoading } = useShiftSummary(today);
  // This cashier's latest orders (any day) — tap one to look it up + reprint.
  const { data: recent } = useOrderLookup({ q: '', date: '', page: 1, perPage: 5, scope: 'own' });
  const recentRows = recent?.rows ?? [];

  const cash = data?.byMethod.cash ?? EMPTY;
  const emoney = data?.byMethod['e-money'] ?? EMPTY;
  const qris = data?.byMethod.midtrans ?? EMPTY;
  const totalGross = cash.gross + emoney.gross + qris.gross;
  const totalCount = cash.count + emoney.count + qris.count;

  const { text: greeting, Icon: GreetIcon } = greetingFor(new Date().getHours());
  const name = user?.name?.trim() || 'Kasir';

  return (
    <div className="mx-auto h-full w-full max-w-5xl overflow-y-auto px-6 py-8 pos-scroll">
      {/* Greeting */}
      <header className="reveal-up flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="flex items-center gap-1.5 font-sans text-xs font-semibold uppercase tracking-[0.18em] text-gold-600">
            <GreetIcon className="h-3.5 w-3.5" aria-hidden="true" /> Beranda kasir
          </p>
          <h1 className="mt-1 font-display text-3xl font-bold tracking-tight text-ink-900">
            {greeting}, {name}.
          </h1>
        </div>
        <span className="rounded-full border border-cream-200 bg-white px-3 py-1.5 text-sm font-medium text-ink-600">
          {formatDate(today)}
        </span>
      </header>

      {/* Takings band — the day's receipt at a glance (signature) */}
      <section
        className="reveal-up mt-6 overflow-hidden rounded-2xl border border-cream-200 bg-white shadow-sm"
        aria-label="Penjualan hari ini"
      >
        <div className="h-1 w-full bg-gradient-to-r from-gold-500 to-gold-600" />
        <div className="flex flex-wrap items-end justify-between gap-4 px-5 pt-4">
          <div>
            <p className="font-sans text-xs font-semibold uppercase tracking-wide text-ink-400">
              Penjualan hari ini
            </p>
            {isLoading ? (
              <Loader2 className="mt-2 h-8 w-8 animate-spin text-gold-500" aria-hidden="true" />
            ) : (
              <MoneyValue value={totalGross} emphasis className="text-4xl leading-none" />
            )}
          </div>
          <div className="text-right">
            <p className="tnum font-display text-2xl font-bold text-ink-900">{number(totalCount)}</p>
            <p className="text-xs text-muted-foreground">transaksi</p>
          </div>
        </div>
        <div className="mt-4 grid grid-cols-3 gap-4 border-t border-dashed border-cream-200 px-5 py-4">
          <MethodStat label="Tunai" count={cash.count} gross={cash.gross} />
          <MethodStat label="E-money" count={emoney.count} gross={emoney.gross} />
          <MethodStat label="QRIS" count={qris.count} gross={qris.gross} />
        </div>
      </section>

      {/* Quick actions */}
      <section className="reveal-up mt-7">
        <h2 className="mb-3 font-display text-sm font-semibold uppercase tracking-wide text-ink-600">
          Aksi cepat
        </h2>
        {/* Seven tiles: "Jual" spans two columns so the grid fills evenly
            (2 cols → 1 + 3 rows; 4 cols → 2 rows of 4 cells). */}
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <ActionTile
            to="/"
            icon={Store}
            title="Jual"
            desc="Mulai transaksi baru"
            primary
            className="col-span-2"
          />
          <ActionTile to="/foc" icon={Gift} title="Tiket FOC" desc="Tiket gratis + dokumen" />
          <ActionTile to="/orders" icon={ClipboardList} title="Pesanan" desc="Cari & cetak ulang tiket" />
          <ActionTile to="/print-ticket" icon={TicketCheck} title="Cetak Tiket" desc="Cetak dari nomor pesanan" />
          <ActionTile to="/manage-addons" icon={ArrowLeftRight} title="Add-on" desc="Transfer & beli add-on" />
          <ActionTile to="/shift" icon={Receipt} title="Shift" desc="Ringkasan & tutup shift" />
          <ActionTile to="/setup" icon={Cog} title="Perangkat" desc="Atur printer & pembaca kartu" />
        </div>
      </section>

      {/* Recent transactions — tap to look up + reprint */}
      <section className="reveal-up mt-7">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="font-display text-sm font-semibold uppercase tracking-wide text-ink-600">
            Transaksi terakhir
          </h2>
          <Link to="/orders" className="text-xs font-medium text-crimson-700 hover:underline">
            Lihat semua
          </Link>
        </div>
        {recentRows.length === 0 ? (
          <p className="rounded-xl border border-dashed border-cream-200 bg-white/50 px-4 py-6 text-center text-sm text-muted-foreground">
            Belum ada transaksi hari ini.
          </p>
        ) : (
          <ul className="divide-y divide-cream-200 overflow-hidden rounded-xl border border-cream-200 bg-white">
            {recentRows.map((row) => (
              <li key={row.invoiceId}>
                <Link
                  to={`/orders?q=${encodeURIComponent(row.invoiceId)}`}
                  className="flex items-center gap-3 px-4 py-2.5 transition-colors hover:bg-cream-100/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-gold-500"
                >
                  <span className="tnum w-14 shrink-0 text-sm text-ink-600">{formatTime(row.createdAt)}</span>
                  <span className="min-w-0 flex-1 truncate font-sans text-sm font-medium text-ink-900">
                    {row.invoiceId}
                  </span>
                  <StatusPill status={row.status} />
                  <MoneyValue value={row.totalAmount} className="w-24 shrink-0 text-right text-sm" />
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Device + shortcuts */}
      <section className="mt-6 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-cream-200 bg-cream-100/60 px-4 py-3">
        <div className="flex flex-wrap items-center gap-4 text-sm">
          <span className="flex items-center gap-1.5">
            <Printer
              className={cn('h-4 w-4', printerBound ? 'text-success' : 'text-ink-400')}
              aria-hidden="true"
            />
            <span className={printerBound ? 'text-ink-600' : 'text-ink-400'}>
              {printerBound ? 'Printer siap' : 'Printer belum diatur'}
            </span>
          </span>
          <span className="flex items-center gap-1.5">
            <CreditCard
              className={cn('h-4 w-4', readerBound ? 'text-success' : 'text-ink-400')}
              aria-hidden="true"
            />
            <span className={readerBound ? 'text-ink-600' : 'text-ink-400'}>
              {readerBound ? 'Pembaca kartu siap' : 'Pembaca kartu belum diatur'}
            </span>
          </span>
          {(!printerBound || !readerBound) && (
            <Link to="/setup" className="text-sm font-medium text-crimson-700 hover:underline">
              Atur perangkat
            </Link>
          )}
        </div>
        <p className="text-xs text-muted-foreground">
          Pintasan:{' '}
          <kbd className="rounded bg-white px-1.5 py-0.5 font-sans text-[11px] text-ink-600 shadow-sm">Ctrl K</kbd>{' '}
          perintah ·{' '}
          <kbd className="rounded bg-white px-1.5 py-0.5 font-sans text-[11px] text-ink-600 shadow-sm">F2</kbd> cari ·{' '}
          <kbd className="rounded bg-white px-1.5 py-0.5 font-sans text-[11px] text-ink-600 shadow-sm">F8</kbd> bayar
        </p>
      </section>
    </div>
  );
}
